import React, { useState, useEffect, useContext, memo, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import extracted components
import UmrahGroupManagement from './components/UmrahGroupManagement';
import DailyOperationsManagement from './components/DailyOperationsManagement';
import OrganizedTripsManagement from './components/OrganizedTripsManagement';
import FinancialManagement from './components/FinancialManagement';
import CashTransfersInboxPage from './components/CashTransfersInboxPage';
import ArchiveManagement from './components/ArchiveManagement';
import TicketsManagement from './components/TicketsManagement';
import RefundManagement from './components/RefundManagement';
import ForeignExchangeManagement from './components/ForeignExchangeManagement';
import GeneralAccountingManagement from './components/GeneralAccountingManagement';
import CreditNotesManagement from './components/CreditNotesManagement';
import PendingApprovalsInbox from './components/PendingApprovalsInbox';
import ExpenseAggregator from './components/ExpenseAggregator';
import SmartArchiveManagement from './components/SmartArchiveManagement';
import OperationsHub from './components/OperationsHub';
import SecureBackupCenter from './components/SecureBackupCenter';
import SecurityCenter from './components/SecurityCenter';
import BackupRestore from './components/BackupRestore';
import { getDeviceFingerprint, getDeviceMeta } from './utils/deviceFingerprint';
import DailyOperationsReports from './components/DailyOperationsReports';
import ServicesManagement from './components/ServicesManagement';
import HierarchicalServicesManagement from './components/HierarchicalServicesManagement';
import CRMDashboard from './components/CRMDashboard';
import SalesPipeline from './components/SalesPipeline';
import WhatsAppFAB from './components/WhatsAppFAB';
import TravelGroupsWidget from './components/TravelGroupsWidget';
import AgencySettingsManagement from './components/AgencySettingsManagement';
import PDFSettingsManagement from './components/PDFSettingsManagement';
import ExcelSettingsManagement from './components/ExcelSettingsManagement';
import TransferAuditTrail from './components/TransferAuditTrail';
import FinancialTraceability from './components/FinancialTraceability';
import VerifyDocument from './components/VerifyDocument';
import DocumentCenter from './components/DocumentCenter';
import SeasonReset from './components/SeasonReset';
import CreateTransferBatch from './components/CreateTransferBatch';
import SimpleInstallmentsManagement from './components/SimpleInstallmentsManagement';
import InstallmentsByAgencyOverview from './components/InstallmentsByAgencyOverview';
import SubagentsManagement from './components/SubagentsManagement';
import FlightsManagement from './components/FlightsManagement';
import Dashboard from './components/Dashboard';
import AgencyFilter from './components/AgencyFilter';
import FormatPickerDialog from './components/FormatPickerDialog';
import DailyReportsManagement from './components/DailyReportsManagement';
import ClientsManagement from './components/ClientsManagement';
import SuppliersManagement from './components/SuppliersManagement';
import BookingsManagement from './components/BookingsManagement';
import InvoicesManagement from './components/InvoicesManagement';
import PaymentsManagement from './components/PaymentsManagement';
import ReportsManagement from './components/ReportsManagement';
import UserManagement from './components/UserManagement';
import AgencyManagement from './components/AgencyManagement';
import PayrollManagement from './components/PayrollManagement';
import ClientDossierManagement from './components/ClientDossierManagement';
import { initResizableColumns } from './utils/resizableColumns';
import { AuthContext, LanguageContext, formatTimeWithEnglishNumerals } from './shared';
import { BUILD_VERSION, BUILD_LABEL } from './buildInfo';

// Import UI components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { CalendarIcon, Home, Users, Building2, Package, FileText, CreditCard, Wallet, BarChart3, Settings, LogOut, Globe, Plus, Search, Edit, Trash2, CheckCircle, XCircle, Clock, Bell, AlertTriangle, Info, AlertCircle, MapPin, Printer, Plane, Menu, X } from 'lucide-react';
import { format } from 'date-fns';
import { ar, fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

console.log('=== APP.JS LOADED ===');
console.log('BACKEND_URL:', BACKEND_URL);
console.log('API:', API);

// Configure axios - Remove global withCredentials to fix CORS issues
// axios.defaults.withCredentials = true; // This causes CORS issues with JWT auth

// Utility function to format dates with English numerals for Arabic UI
const formatDateWithEnglishNumerals = (date, options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };
  
  // Use 'en-US' locale to get English numerals but keep Arabic text if needed
  return new Date(date).toLocaleDateString('en-GB', defaultOptions);
};


// Translation object - Enhanced Professional Arabic UI
const translations = {
  ar: {
    // Main Navigation - القوائم الرئيسية
    dashboard: '🏠 لوحة التحكم الرئيسية',
    clients: '👥 إدارة العملاء',
    suppliers: '🏪 إدارة الموردين', 
    bookings: '📋 إدارة الحجوزات',
    invoices: '📄 إدارة الفواتير',
    payments: '💳 إدارة المدفوعات',
    reports: '📊 التقارير والإحصائيات',
    userManagement: '👤 إدارة المستخدمين',
    dailyReports: '📈 التقارير اليومية',
    settings: '⚙️ إعدادات النظام',
    agencySettings: '🏢 إعدادات الوكالات',
    logout: '🚪 تسجيل الخروج',
    
    // Agency Settings - إعدادات الوكالات
    agencyInformation: 'معلومات الوكالة',
    basicInformation: 'المعلومات الأساسية',
    contactInformation: 'معلومات الاتصال',
    registrationDetails: 'تفاصيل التسجيل',
    managementInfo: 'معلومات الإدارة',
    brandingSettings: 'إعدادات العلامة التجارية',
    agencyName: 'اسم الوكالة',
    agencyAddress: 'عنوان الوكالة',
    agencyCity: 'المدينة',
    postalCode: 'الرمز البريدي',
    primaryPhone: 'الهاتف الأساسي',
    secondaryPhone: 'الهاتف الثانوي',
    additionalPhone: 'هاتف إضافي',
    faxNumber: 'رقم الفاكس',
    agencyEmail: 'البريد الإلكتروني',
    agencyWebsite: 'موقع الوكالة',
    taxNumber: 'رقم التسجيل الضريبي',
    commercialRegister: 'رقم السجل التجاري',
    nationalRegister: 'رقم السجل الوطني',
    businessLicense: 'رخصة النشاط التجاري',
    managerName: 'اسم المدير',
    establishedDate: 'تاريخ التأسيس',
    agencyDescription: 'وصف الوكالة',
    logoUrl: 'رابط الشعار',
    headerText: 'نص الرأس',
    footerText: 'نص التذييل',
    managerSignature: 'توقيع المدير',
    saveSettings: 'حفظ الإعدادات',
    settingsUpdated: 'تم تحديث الإعدادات بنجاح',
    settingsUpdateFailed: 'فشل في تحديث الإعدادات',
    
    // NEW - Services and Operations Management
    servicesManagement: '🛠️ إدارة الخدمات',
    dailyOperations: '📋 العمليات اليومية',
    dailyOperationsReports: '📊 تقارير العمليات',
    
    // Sub-Navigation - القوائم الفرعية
    financialManagement: '💰 الإدارة المالية',
    systemAdministration: '🔧 إدارة النظام',
    operationsManagement: '📋 إدارة العمليات',
    reportsCenter: '📊 مركز التقارير',
    
    // Dashboard Sections - أقسام لوحة التحكم
    quickStats: '📈 الإحصائيات السريعة',
    todayOverview: '📅 نظرة عامة على اليوم',
    weeklyOverview: '📆 نظرة عامة على الأسبوع',
    monthlyOverview: '🗓️ نظرة عامة على الشهر',
    
    // Roles - الأدوار والصلاحيات
    superAdmin: '👑 المدير العام للنظام',
    generalAccountant: '💼 المحاسب العام',
    agencyStaff: '🏢 موظف الوكالة',
    
    // Authentication - المصادقة
    login: 'تسجيل الدخول',
    welcome: 'مرحباً بك',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    loginSuccess: 'تم تسجيل الدخول بنجاح',
    loginFailed: 'فشل في تسجيل الدخول',
    
    // Dashboard Cards - بطاقات لوحة التحكم
    todayIncome: '💰 إيرادات اليوم',
    unpaidInvoices: '⚠️ الفواتير غير المسددة',
    weekBookings: '📅 حجوزات هذا الأسبوع',
    cashboxBalance: '🏦 إجمالي رصيد الصناديق',
    activeClients: '👥 العملاء النشطين',
    pendingPayments: '⏳ المدفوعات المعلقة',
    
    // Reports Management - إدارة التقارير
    createReport: '➕ إنشاء تقرير جديد',
    approveReport: '✅ الموافقة على التقرير',
    rejectReport: '❌ رفض التقرير',
    pendingApproval: '⏳ في انتظار الموافقة',
    approved: '✅ تمت الموافقة',
    rejected: '❌ مرفوض',
    reportsList: '📋 قائمة التقارير',
    generateReport: '🔄 إنتاج التقرير',
    
    // NEW - Services Management
    servicesList: '📋 قائمة الخدمات',
    addService: '➕ إضافة خدمة جديدة',
    editService: '✏️ تعديل الخدمة',
    deleteService: '🗑️ حذف الخدمة',
    serviceName: 'اسم الخدمة',
    serviceType: 'نوع الخدمة',
    serviceCategory: 'تصنيف الخدمة',
    basePrice: 'السعر الأساسي (دج)',
    minPrice: 'أقل سعر مسموح (دج)',
    isFixedPrice: 'سعر ثابت',
    isVariablePrice: 'سعر متغير',
    priceType: 'نوع السعر',
    fixedPriceService: 'خدمة بسعر ثابت',
    variablePriceService: 'خدمة بسعر متغير',
    pricePermissions: 'صلاحيات السعر',
    isActive: 'نشطة',
    serviceDescription: 'وصف الخدمة',
    
    // NEW - Daily Operations
    operationsList: '📋 قائمة العمليات اليومية',
    addOperation: '➕ إضافة عملية جديدة',
    operationNo: 'رقم الوصل',
    operationDate: 'تاريخ العملية',
    clientName: 'اسم العميل',
    serviceName: 'اسم الخدمة',
    discountAmount: 'مبلغ التخفيض (دج)',
    discountReason: 'سبب التخفيض',
    finalPrice: 'السعر النهائي (دج)',
    operationStatus: 'حالة العملية',
    approveOperation: '✅ اعتماد العملية',
    rejectOperation: '❌ رفض العملية',
    
    // NEW - Operation Statuses
    draft: '📝 مسودة',
    pendingApproval: '⏳ في انتظار الموافقة',
    operationApproved: '✅ معتمد',
    operationRejected: '❌ مرفوض',
    
    // NEW - Service Types
    umrah: 'عمرة',
    hajj: 'حج',
    flightTicket: 'تذكرة طيران',
    hotelBooking: 'حجز فندق',
    visaService: 'خدمة تأشيرة',
    transport: 'نقل',
    insurance: 'تأمين',
    passportService: 'خدمة جواز سفر',
    otherService: 'أخرى',
    
    // NEW - Service Categories
    religiousServices: 'خدمات دينية',
    travelServices: 'خدمات سفر',
    documentationServices: 'خدمات وثائق',
    accommodationServices: 'خدمات إقامة',
    otherCategory: 'أخرى',
    
    // User Management - إدارة المستخدمين  
    addUser: '➕ إضافة مستخدم جديد',
    editUser: '✏️ تعديل بيانات المستخدم',
    deleteUser: '🗑️ حذف المستخدم',
    selectRole: 'اختيار الدور الوظيفي',
    selectAgency: 'اختيار الوكالة',
    usersList: '👥 قائمة المستخدمين',
    userDetails: '📋 تفاصيل المستخدم',
    
    // Common Actions - الإجراءات العامة
    name: 'الاسم الكامل',
    phone: 'رقم الهاتف',
    add: '➕ إضافة',
    edit: '✏️ تعديل',
    delete: '🗑️ حذف',
    save: '💾 حفظ',
    cancel: '❌ إلغاء',
    actions: 'الإجراءات المتاحة',
    status: 'الحالة الحالية',
    date: 'التاريخ',
    amount: 'المبلغ بالدينار',
    search: '🔍 البحث',
    filter: '🔽 تصفية',
    export: '📤 تصدير البيانات',
    import: '📥 استيراد البيانات',
    loading: '⏳ جاري التحميل...',
    noData: '📭 لا توجد بيانات للعرض',
    total: 'المجموع الكلي',
    profit: 'صافي الربح',
    view: '👁️ عرض',
    details: '📋 التفاصيل',
    
    // Clients Management - إدارة العملاء
    addClient: '➕ إضافة عميل جديد',
    editClient: '✏️ تعديل بيانات العميل',
    deleteClient: '🗑️ حذف العميل',
    cinPassport: '🆔 رقم الهوية/جواز السفر',
    clientsList: '👥 قائمة العملاء',
    clientDetails: '📋 تفاصيل العميل',
    clientHistory: '📚 تاريخ معاملات العميل',
    
    // Suppliers Management - إدارة الموردين
    addSupplier: '➕ إضافة مورد جديد',
    editSupplier: '✏️ تعديل بيانات المورد',
    deleteSupplier: '🗑️ حذف المورد',
    supplierType: '🏷️ تصنيف المورد',
    contact: '📞 بيانات الاتصال',
    suppliersList: '🏪 قائمة الموردين',
    supplierDetails: '📋 تفاصيل المورد',
    
    // Bookings Management - إدارة الحجوزات
    addBooking: '➕ إنشاء حجز جديد',
    editBooking: '✏️ تعديل الحجز',
    deleteBooking: '🗑️ إلغاء الحجز',
    reference: '🔢 رقم المرجع',
    client: '👤 العميل',
    supplier: '🏪 المورد',
    bookingType: '🏷️ نوع الخدمة',
    cost: '💸 تكلفة الخدمة',
    sellPrice: '💰 سعر البيع للعميل',
    startDate: '📅 تاريخ بداية الخدمة',
    endDate: '📅 تاريخ انتهاء الخدمة',
    bookingsList: '📋 سجل الحجوزات',
    selectClient: 'اختيار العميل',
    selectSupplier: 'اختيار المورد',
    bookingDetails: '📋 تفاصيل الحجز',
    
    // Service Types - أنواع الخدمات
    'عمرة': '🕋 عمرة',
    'طيران': '✈️ تذاكر طيران',
    'فندق': '🏨 حجز فنادق',
    'تأشيرة': '📋 خدمات التأشيرات',
    
    // Invoices Management - إدارة الفواتير
    addInvoice: '➕ إصدار فاتورة جديدة',
    editInvoice: '✏️ تعديل الفاتورة',
    deleteInvoice: '🗑️ حذف الفاتورة',
    invoiceNo: '🔢 رقم الفاتورة',
    amountHT: '💰 المبلغ قبل الضريبة',
    tvaRate: '📊 معدل ضريبة القيمة المضافة %',
    amountTTC: '💳 المبلغ شامل الضريبة',
    dueDate: '⏰ تاريخ استحقاق السداد',
    invoicesList: '📄 سجل الفواتير',
    generateFromBooking: '🔄 إنشاء فاتورة من الحجز',
    printInvoice: '🖨️ طباعة الفاتورة',
    sendInvoice: '📧 إرسال الفاتورة',
    
    // Invoice Status - حالة الفواتير
    pending: '⏳ في انتظار السداد',
    paid: '✅ مسددة بالكامل',
    overdue: '⚠️ متأخرة السداد',
    partial: '🔄 مسددة جزئياً',
    
    // Payments Management - إدارة المدفوعات
    addPayment: '➕ تسجيل دفعة جديدة',
    editPayment: '✏️ تعديل الدفعة',
    deletePayment: '🗑️ حذف سجل الدفعة',
    paymentNo: '🔢 رقم عملية الدفع',
    invoice: '📄 الفاتورة المرتبطة',
    paymentMethod: '💳 طريقة الدفع',
    paymentDate: '📅 تاريخ الدفع',
    paymentsList: '💰 سجل المدفوعات',
    selectInvoice: 'اختيار الفاتورة',
    remainingAmount: '📊 المبلغ المتبقي',
    
    // Payment Methods - طرق الدفع
    cash: '💵 نقداً',
    bank: '🏦 حوالة بنكية', 
    card: '💳 بطاقة ائتمانية',
    check: '📝 شيك',
    
    // Reports Center - مركز التقارير
    dailySalesReport: '📈 تقرير المبيعات اليومية',
    monthlySalesReport: '📊 تقرير المبيعات الشهرية',
    agingReport: '⏰ تقرير أعمار الديون',
    profitLossReport: '💹 تقرير الأرباح والخسائر',
    cashFlowReport: '💰 تقرير التدفق النقدي',
    clientsReport: '👥 تقرير العملاء',
    suppliersReport: '🏪 تقرير الموردين',
    generateReport: '🔄 إنتاج التقرير',
    reportPeriod: '📅 فترة التقرير',
    from: 'من تاريخ',
    to: 'إلى تاريخ',
    
    // System Messages - رسائل النظام
    success: '✅ تم بنجاح',
    error: '❌ حدث خطأ',
    warning: '⚠️ تحذير',
    info: 'ℹ️ معلومة',
    confirm: '✔️ تأكيد',
    confirmDelete: '🗑️ هل أنت متأكد من الحذف؟',
    dataRequired: '⚠️ يرجى إدخال البيانات المطلوبة',
    saveSuccess: '💾 تم الحفظ بنجاح',
    updateSuccess: '✅ تم التحديث بنجاح',
    deleteSuccess: '🗑️ تم الحذف بنجاح',
    
    // Financial Terms - المصطلحات المالية
    revenue: '📈 الإيرادات',
    expenses: '📉 المصروفات',
    netProfit: '💰 صافي الربح',
    grossProfit: '💸 إجمالي الربح',
    commission: '💼 العمولة',
    discount: '🏷️ الخصم',
    balance: '⚖️ الرصيد',
    debt: '📋 الدين',
    credit: '💳 دائن',
    debit: '💸 مدين'
  },
  fr: {
    // Navigation and roles
    dashboard: 'Tableau de bord',
    userManagement: 'Gestion des utilisateurs',
    dailyReports: 'Rapports quotidiens',
    superAdmin: 'Super Admin',
    generalAccountant: 'Comptable Général',
    agencyStaff: 'Personnel Agence',
    
    // All other translations...
    clients: 'Clients',
    suppliers: 'Fournisseurs',
    bookings: 'Réservations',
    invoices: 'Factures',
    payments: 'Paiements',
    reports: 'Rapports',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    login: 'Connexion',
    email: 'Email',
    password: 'Mot de passe',
    todayIncome: "Revenus d'aujourd'hui",
    unpaidInvoices: 'Factures impayées',
    weekBookings: 'Réservations de la semaine',
    cashboxBalance: 'Solde de caisse',
    name: 'Nom',
    phone: 'Téléphone',
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    actions: 'Actions',
    status: 'Statut',
    date: 'Date',
    amount: 'Montant',
    search: 'Rechercher',
    loading: 'Chargement...',
    noData: 'Aucune donnée',
    addClient: 'Ajouter un client',
    cinPassport: 'CIN/Passeport',
    clientsList: 'Liste des clients',
    addSupplier: 'Ajouter un fournisseur',
    supplierType: 'Type de fournisseur',
    contact: 'Contact',
    suppliersList: 'Liste des fournisseurs',
    addBooking: 'Ajouter une réservation',
    reference: 'Référence',
    client: 'Client',
    supplier: 'Fournisseur',
    bookingType: 'Type de réservation',
    cost: 'Coût',
    sellPrice: 'Prix de vente',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    bookingsList: 'Liste des réservations',
    'عمرة': 'Omra',
    'طيران': 'Vol',
    'فندق': 'Hôtel',
    'تأشيرة': 'Visa',
    addInvoice: 'Ajouter une facture',
    invoiceNo: 'N° Facture',
    amountHT: 'Montant HT',
    tvaRate: 'Taux TVA %',
    amountTTC: 'Montant TTC',
    dueDate: "Date d'échéance",
    invoicesList: 'Liste des factures',
    pending: 'En attente',
    paid: 'Payée',
    overdue: 'En retard',
    addPayment: 'Ajouter un paiement',
    paymentNo: 'N° Paiement',
    invoice: 'Facture',
    paymentMethod: 'Méthode de paiement',
    paymentDate: 'Date de paiement',
    paymentsList: 'Liste des paiements',
    cash: 'Espèces',
    bank: 'Banque',
    card: 'Carte',
    success: 'Succès',
    error: 'Erreur',
    confirm: 'Confirmer',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer?'
  }
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ar');
  const [isRTL, setIsRTL] = useState(true);

  const toggleLanguage = () => {
    const newLang = language === 'ar' ? 'fr' : 'ar';
    setLanguage(newLang);
    setIsRTL(newLang === 'ar');
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const t = (key) => translations[language][key] || key;

  useEffect(() => {
    document.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  return (
    <LanguageContext.Provider value={{ language, isRTL, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enhanced authentication context with Google OAuth support
  const login = async (email, password) => {
    try {
      // Capture device fingerprint so the backend's Trusted Devices engine
      // can detect new/unknown machines and require admin approval.
      const fp = getDeviceFingerprint();
      const meta = getDeviceMeta();

      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        device_fingerprint: fp,
        device_name: meta.device_name,
        user_agent: meta.user_agent,
        screen: meta.screen,
        timezone: meta.timezone,
      });
      const { access_token, user: userData } = response.data;

      if (!access_token) {
        return { success: false, error: 'No access token received' };
      }

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      // 🆕 Refetch /auth/me to pick up enrichment fields (is_subagent,
      // agency_name) that aren't part of the login response payload.
      try {
        const me = await axios.get(`${API}/auth/me`);
        setUser(me.data);
      } catch (_) {
        setUser(userData);
      }
      return { success: true };
    } catch (error) {
      // Surface security-aware errors so the UI can show a friendly screen
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.reason) {
        return {
          success: false,
          error: detail.message || 'Access denied',
          security_reason: detail.reason,
          device_id: detail.device_id,
        };
      }
      return { success: false, error: detail || 'Login failed' };
    }
  };

  // Google Authentication function
  const loginWithGoogle = () => {
    console.log('=== GOOGLE LOGIN START ==='); 
    const currentUrl = window.location.origin;
    const redirectUrl = `${currentUrl}/profile`;
    const googleAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    console.log('Redirecting to Google Auth:', googleAuthUrl);
    window.location.href = googleAuthUrl;
  };

  // Handle Google OAuth callback
  const handleGoogleCallback = async (sessionId) => {
    try {
      console.log('=== GOOGLE CALLBACK START ===');
      console.log('Session ID received:', sessionId);
      
      const response = await axios.post(`${API}/auth/google`, { session_id: sessionId }, {
        withCredentials: true // Important for cookies
      });
      
      console.log('Google auth response:', response.data);
      
      const { user: userData, session_token } = response.data;
      
      // Set user data in context
      setUser(userData);
      
      console.log('Google authentication successful:', userData);
      console.log('=== GOOGLE CALLBACK SUCCESS ===');
      return { success: true };
    } catch (error) {
      console.error('=== GOOGLE CALLBACK ERROR ===');
      console.error('Google auth error:', error);
      return { success: false, error: error.response?.data?.detail || 'Google authentication failed' };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear session cookie
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if server call fails
    }
    
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(response => setUser(response.data))
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      loginWithGoogle, 
      handleGoogleCallback, 
      logout, 
      loading 
    }}>
      <SecurityWatchdog user={user} onForcedLogout={logout} />
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Security watchdog: every 30 seconds while logged in, ping /api/auth/me
 * to detect server-side session revocation (admin force-logout, account
 * suspension, device blocked). On 401/423, immediately log the user out
 * and show an Arabic message — matches the user's requirement that any
 * admin action takes effect "real-time without refresh".
 */
const SecurityWatchdog = ({ user, onForcedLogout }) => {
  React.useEffect(() => {
    if (!user) return;
    let aborted = false;
    const check = async () => {
      try {
        await axios.get(`${API}/auth/me`);
      } catch (e) {
        if (aborted) return;
        const code = e.response?.status;
        if (code === 401 || code === 423) {
          const msg = e.response?.data?.detail || 'تم إنهاء جلستك من طرف الإدارة.';
          alert(`🔒 ${typeof msg === 'string' ? msg : 'تم إنهاء جلستك من طرف الإدارة.'}`);
          await onForcedLogout();
        }
      }
    };
    const t = setInterval(check, 30000); // 30s
    return () => { aborted = true; clearInterval(t); };
  }, [user, onForcedLogout]);
  return null;
};

// Simple Login Component (with Google OAuth support)
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useContext(AuthContext);
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  // Simple test function
  const testFunction = () => {
    console.log('=== TEST FUNCTION CALLED ===');
    alert('Test function works!');
  };

  const handleLogin = async () => {
    console.log('=== HANDLE LOGIN CALLED ===');
    console.log('Email:', email);
    console.log('Password length:', password.length);
    
    setLoading(true);
    setError('');
    
    try {
      console.log('About to call login function...');
      const result = await login(email, password);
      console.log('Login function returned:', result);
      
      if (result.success) {
        console.log('Login successful, reloading page to ensure state refresh...');
        // Reload page to ensure clean state
        window.location.href = '/';
      } else {
        console.log('Login failed with error:', result.error);
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error caught:', error);
      setError('An error occurred during login');
    }
    
    setLoading(false);
    console.log('=== HANDLE LOGIN FINISHED ===');
  };

  const handleGoogleLogin = () => {
    console.log('=== GOOGLE LOGIN CLICKED ===');
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-brand-100">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center font-bold text-gray-800">
            {t('login')}
          </CardTitle>
          <CardDescription className="text-center text-gray-600">
            نظام محاسبة وكالات صنهاجة للسفر - غرب الجزائر
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Google Login Button */}
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              🔐 تسجيل الدخول باستخدام Google
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-brand-100 px-2 text-gray-500">أو</span>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-2">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="text-right"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="text-right"
                />
              </div>
              {error && (
                <div className="text-red-500 text-sm text-center">{error}</div>
              )}
              
              {/* Traditional Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-9 px-4 py-2 bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700 text-white rounded-md font-medium disabled:opacity-50"
                data-testid="regular-login-button"
              >
                {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول العادي'}
              </button>
            </form>
          </div>
          {/* Build version indicator - visible on production so you can confirm which build is live */}
          <div className="mt-4 pt-3 border-t border-gray-200 text-center" data-testid="build-version-footer">
            <div className="text-[10px] text-gray-400 font-mono tracking-wide" dir="ltr">
              build: {BUILD_VERSION}
            </div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              {BUILD_LABEL}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Layout Component
const Layout = ({ children, activeTab, setActiveTab }) => {
  const { user, logout } = useContext(AuthContext);
  const { language, toggleLanguage, t } = useContext(LanguageContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close mobile sidebar whenever the active tab changes
  useEffect(() => { setSidebarOpen(false); }, [activeTab]);

  // Close sidebar on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Navigation items based on user role - Enhanced Professional Organization
  const getNavItems = () => {
    const baseItems = [
      { id: 'dashboard', label: t('dashboard'), icon: Home, category: 'main' }
    ];

    // Super Admin sees everything with organized categories
    if (user?.role === 'super_admin') {
      return [
        ...baseItems,
        // System Administration Category
        { 
          category: 'systemAdmin',
          label: t('systemAdministration'),
          items: [
            { id: 'userManagement', label: t('userManagement'), icon: Settings },
            { id: 'agencyManagement', label: '🏢 إدارة الوكالات', icon: Building2 },
            { id: 'dailyReports', label: t('dailyReports'), icon: BarChart3 },
            { id: 'servicesManagement', label: t('servicesManagement'), icon: Package },
            { id: 'hierarchical-services', label: '🗂️ الخدمات الهرمية (موسم جديد)', icon: Package },
            { id: 'smart-archive', label: '🗄️ مركز الأرشفة الذكي', icon: Package },
            { id: 'ops-hub', label: '🌐 مركز العمليات الموحّد', icon: BarChart3 },
            { id: 'secure-backup', label: '🛡️ مركز النسخ الآمن', icon: Settings },
            { id: 'full-backup-restore', label: '💾 نسخة كاملة + استرجاع (.sanhadja)', icon: Settings },
            { id: 'security-center', label: '🔐 مركز الأمان (الأجهزة + الجلسات)', icon: Settings },
            { id: 'pdfSettings', label: '🧾 إعدادات PDF (تصميم الوصولات)', icon: FileText },
            { id: 'excelSettings', label: '📊 إعدادات Excel (تصميم الملفات)', icon: FileText },
            { id: 'transferAuditTrail', label: '🔍 تدقيق الوصولات', icon: FileText },
            { id: 'documentCenter', label: '📋 مركز الوثائق (Audit Trail)', icon: FileText },
            { id: 'season-reset', label: '🔄 إغلاق الموسم وبدء موسم جديد', icon: CalendarIcon },
            { id: 'subagents-management', label: '🤝 الوكلاء المُناوِلون (B2B)', icon: Users },
            { id: 'agencySettings', label: t('agencySettings'), icon: Building2 }
          ]
        },
        // Operations Management Category  
        {
          category: 'operations',
          label: t('operationsManagement'),
          items: [
            { id: 'clients', label: t('clients'), icon: Users },
            { id: 'sales-pipeline', label: '📋 Sales Pipeline', icon: Users },
            { id: 'crm-dashboard', label: '📊 لوحة CRM', icon: BarChart3 },
            { id: 'bookings', label: t('bookings'), icon: Package },
            { id: 'tickets', label: '✈️ التذاكر', icon: FileText },
            { id: 'organized-trips', label: '🗺️ الرحلات المنظمة', icon: MapPin },
            { id: 'umrah-groups', label: '🛫 إدارة الأفواج (حج/عمرة/سياحية/أخرى)', icon: Users },
            { id: 'flights-management', label: '🛩️ إدارة رحلات الطيران', icon: Plane, customGradient: 'from-sky-500 via-indigo-500 to-violet-600', customAccent: 'sky', isHighlighted: true },
            { id: 'suppliers', label: '🏪 إدارة الموردين', icon: Building2 },
            { id: 'dailyOperations', label: t('dailyOperations'), icon: FileText }
          ]
        },
        // Financial Management Category
        {
          category: 'financial',
          label: t('financialManagement'),
          items: [
            { id: 'invoices', label: t('invoices'), icon: FileText },
            { id: 'payments', label: t('payments'), icon: CreditCard },
            { id: 'financial-management', label: '💰 الإدارة المالية', icon: CreditCard },
            { id: 'cash-transfers-inbox', label: '📥 التحويلات النقدية الواردة', icon: CreditCard },
            { id: 'archive-management', label: '🗄️ مركز الأرشيف', icon: CreditCard },
            { id: 'cashbox', label: '🏦 إدارة الخزينة', icon: CreditCard },
            { id: 'general-accounting', label: '📋 محاسبة الإدارة العامة', icon: CreditCard },
            { id: 'credit-notes', label: '💸 سندات التخفيض', icon: FileText },
            { id: 'pending-approvals', label: '📥 صندوق الموافقات', icon: FileText },
            { id: 'expense-aggregator', label: '📊 مجمع التخفيضات', icon: BarChart3 },
            { id: 'foreign-exchange', label: '💱 العملات الأجنبية', icon: Globe },
            { id: 'simple-installments', label: '📋 نظام الأقساط', icon: CalendarIcon },
            { id: 'installments-by-agency', label: '📊 الأقساط حسب الوكالة', icon: BarChart3 },
            { id: 'refunds', label: '↩️ إدارة الاسترجاع', icon: CreditCard },
            { id: 'payroll', label: '💰 الرواتب والأجور (سرّي)', icon: Wallet },
            { id: 'client-dossier', label: '🗂️ ملفات العملاء (Pro)', icon: Users },
            { id: 'reports', label: t('reports'), icon: BarChart3 },
            { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 }
          ]
        }
      ];
    }

    // General Accountant sees all operations and financial functions with full cross-agency access
    if (user?.role === 'general_accountant') {
      return [
        ...baseItems,
        // Reports and Approval Category
        {
          category: 'reportsCenter',
          label: t('reportsCenter'),
          items: [
            { id: 'dailyReports', label: t('dailyReports'), icon: BarChart3 },
            { id: 'reports', label: t('reports'), icon: BarChart3 },
            { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 },
            { id: 'servicesManagement', label: t('servicesManagement'), icon: Package },
            { id: 'hierarchical-services', label: '🗂️ الخدمات الهرمية (موسم جديد)', icon: Package },
            // 🔒 The following admin/system tools are NOT part of the
            //    accountant's day-to-day responsibilities and were removed
            //    from this role's sidebar per user request (2026-07-02):
            //    smart-archive, ops-hub, secure-backup,
            //    full-backup-restore, security-center. They remain
            //    accessible for super_admin only.
            { id: 'pdfSettings', label: '🧾 إعدادات PDF (تصميم الوصولات)', icon: FileText },
            { id: 'excelSettings', label: '📊 إعدادات Excel (تصميم الملفات)', icon: FileText },
            { id: 'transferAuditTrail', label: '🔍 تدقيق الوصولات', icon: FileText },
            { id: 'documentCenter', label: '📋 مركز الوثائق (Audit Trail)', icon: FileText },
            { id: 'agencySettings', label: t('agencySettings'), icon: Building2 }
          ]
        },
        // Full Operations Management (All Agencies)
        {
          category: 'operations',
          label: t('operationsManagement') + ' 🌐',
          items: [
            { id: 'clients', label: t('clients') + ' (جميع الوكالات)', icon: Users },
            { id: 'sales-pipeline', label: '📋 Sales Pipeline', icon: Users },
            { id: 'crm-dashboard', label: '📊 لوحة CRM', icon: BarChart3 },
            { id: 'bookings', label: t('bookings') + ' (جميع الوكالات)', icon: Package },
            { id: 'tickets', label: '✈️ التذاكر (جميع الوكالات)', icon: FileText },
            { id: 'organized-trips', label: '🗺️ الرحلات المنظمة (جميع الوكالات)', icon: MapPin },
            { id: 'umrah-groups', label: '🛫 إدارة الأفواج (حج/عمرة/سياحية/أخرى)', icon: Users },
            { id: 'flights-management', label: '🛩️ إدارة رحلات الطيران', icon: Plane, customGradient: 'from-sky-500 via-indigo-500 to-violet-600', customAccent: 'sky', isHighlighted: true },
            { id: 'suppliers', label: '🏪 إدارة الموردين', icon: Building2 },
            { id: 'dailyOperations', label: t('dailyOperations') + ' (جميع الوكالات)', icon: FileText }
          ]
        },
        // Full Financial Management (All Agencies)
        {
          category: 'financial',
          label: t('financialManagement') + ' 🌐',
          items: [
            { id: 'invoices', label: t('invoices') + ' (جميع الوكالات)', icon: FileText },
            { id: 'payments', label: t('payments') + ' (جميع الوكالات)', icon: CreditCard },
            { id: 'financial-management', label: '💰 الإدارة المالية', icon: CreditCard },
            { id: 'cash-transfers-inbox', label: '📥 التحويلات النقدية الواردة', icon: CreditCard },
            { id: 'archive-management', label: '🗄️ مركز الأرشيف', icon: CreditCard },
            // 🔒 Removed 'cashbox' from accountant sidebar per user
            //    request (2026-07-02). The cashbox remains accessible
            //    to super_admin only.
            { id: 'general-accounting', label: '📋 محاسبة الإدارة العامة', icon: CreditCard },
            { id: 'credit-notes', label: '💸 سندات التخفيض', icon: FileText },
            { id: 'pending-approvals', label: '📥 صندوق الموافقات', icon: FileText },
            { id: 'expense-aggregator', label: '📊 مجمع التخفيضات', icon: BarChart3 },
            { id: 'foreign-exchange', label: '💱 العملات الأجنبية', icon: Globe },
            { id: 'simple-installments', label: '📋 نظام الأقساط', icon: CalendarIcon },
            { id: 'installments-by-agency', label: '📊 الأقساط حسب الوكالة', icon: BarChart3 },
            { id: 'refunds', label: '↩️ إدارة الاسترجاع', icon: CreditCard },
            { id: 'payroll', label: '💰 الرواتب والأجور (سرّي)', icon: Wallet },
            { id: 'client-dossier', label: '🗂️ ملفات العملاء (Pro)', icon: Users }
          ]
        }
      ];
    }

    // 🆕 2026-07-22 — General Manager (مسؤول عام): between services_manager
    // and general_accountant. Same catalog rights as services_manager,
    // PLUS full Umrah groups (الأفواج), full Flights, full Suppliers.
    // Does NOT get financial approvals / archive / user mgmt.
    if (user?.role === 'general_manager') {
      return [
        ...baseItems,
        {
          category: 'operations',
          label: 'العمليات — مسؤول عام',
          items: [
            { id: 'clients', label: t('clients'), icon: Users },
            { id: 'sales-pipeline', label: '📋 Sales Pipeline', icon: Users },
            { id: 'crm-dashboard', label: '📊 لوحة CRM', icon: BarChart3 },
            { id: 'servicesManagement', label: t('servicesManagement'), icon: Package },
            { id: 'hierarchical-services', label: '🗂️ الخدمات الهرمية (موسم جديد)', icon: Package },
            { id: 'tickets', label: '✈️ التذاكر', icon: FileText },
            { id: 'organized-trips', label: '🗺️ الرحلات المنظمة', icon: MapPin },
            { id: 'umrah-groups', label: '🛫 إدارة الأفواج (حج/عمرة/سياحية/أخرى)', icon: Users },
            { id: 'flights', label: '✈️ إدارة رحلات الطيران', icon: Package },
            { id: 'suppliers', label: t('suppliers'), icon: Building2 },
            { id: 'bookings', label: t('bookings'), icon: Package },
            { id: 'dailyOperations', label: t('dailyOperations'), icon: FileText },
          ]
        },
        {
          category: 'financial',
          label: t('financialManagement'),
          items: [
            { id: 'payments', label: '💰 المدفوعات (العمليات)', icon: CreditCard },
            { id: 'financial-management', label: '💰 الإدارة المالية', icon: CreditCard },
            { id: 'general-accounting', label: '📋 محاسبة الإدارة العامة', icon: CreditCard },
            { id: 'pending-approvals', label: '📋 طلباتي (سندات/تغييرات)', icon: FileText },
            { id: 'simple-installments', label: '📋 نظام الأقساط', icon: CalendarIcon },
            { id: 'refunds', label: '↩️ إدارة الاسترجاع', icon: CreditCard },
            { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 },
          ]
        },
        {
          category: 'agencyInfo',
          label: 'معلومات الوكالة',
          items: [
            { id: 'agencySettings', label: t('agencySettings') + ' (للعرض فقط)', icon: Building2 }
          ]
        }
      ];
    }

    // Services Manager — catalog/operations only, NO financial powers
  if (user?.role === 'services_manager') {
    return [
      ...baseItems,
      // Operations — full staff scope + extra catalog-create privileges
      {
        category: 'operations',
        label: t('operationsManagement') + ' — مسؤول الخدمات',
        items: [
          { id: 'clients', label: t('clients'), icon: Users },
          { id: 'sales-pipeline', label: '📋 Sales Pipeline', icon: Users },
          { id: 'crm-dashboard', label: '📊 لوحة CRM', icon: BarChart3 },
          { id: 'servicesManagement', label: t('servicesManagement'), icon: Package },
          { id: 'hierarchical-services', label: '🗂️ الخدمات الهرمية (موسم جديد)', icon: Package },
          { id: 'tickets', label: '✈️ التذاكر', icon: FileText },
          { id: 'organized-trips', label: '🗺️ الرحلات المنظمة', icon: MapPin },
          { id: 'umrah-groups', label: '🛫 إدارة الأفواج (حج/عمرة/سياحية/أخرى)', icon: Users },
          { id: 'suppliers', label: t('suppliers'), icon: Building2 },
          { id: 'bookings', label: t('bookings'), icon: Package },
          { id: 'dailyOperations', label: t('dailyOperations'), icon: FileText },
        ]
      },
      // Financial Transactions — same scope as agency_staff (no approvals)
      {
        category: 'financial',
        label: t('financialManagement'),
        items: [
          { id: 'payments', label: '💰 المدفوعات (العمليات)', icon: CreditCard },
          { id: 'financial-management', label: '💰 الإدارة المالية', icon: CreditCard },
          { id: 'general-accounting', label: '📋 محاسبة الإدارة العامة', icon: CreditCard },
          { id: 'pending-approvals', label: '📋 طلباتي (سندات/تغييرات)', icon: FileText },
          { id: 'simple-installments', label: '📋 نظام الأقساط', icon: CalendarIcon },
          { id: 'refunds', label: '↩️ إدارة الاسترجاع', icon: CreditCard },
          { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 },
        ]
      },
      {
        category: 'agencyInfo',
        label: 'معلومات الوكالة',
        items: [
          { id: 'agencySettings', label: t('agencySettings') + ' (للعرض فقط)', icon: Building2 }
        ]
      }
    ];
  }

  // Agency Staff sees operational functions (LIMITED ACCESS)
    // 🆕 Subagent staff (external partner agencies) get an even tighter
    // sidebar: NO suppliers, NO general accounting (managers settle from
    // their side), NO settings, NO installments. They only sell + transfer.
    if (user?.is_subagent) {
      return [
        ...baseItems,
        {
          category: 'operations',
          label: 'العمليات (وكيل مُناوِل)',
          items: [
            { id: 'clients', label: t('clients'), icon: Users },
            { id: 'tickets', label: '✈️ التذاكر', icon: FileText },
            { id: 'organized-trips', label: '🗺️ الرحلات المنظمة', icon: MapPin },
            { id: 'bookings', label: t('bookings'), icon: Package },
            { id: 'dailyOperations', label: t('dailyOperations'), icon: FileText },
          ],
        },
        {
          category: 'financial',
          label: 'المالية',
          items: [
            { id: 'payments', label: '💰 المدفوعات', icon: CreditCard },
            { id: 'financial-management', label: '💸 تحويل النقدية للإدارة', icon: CreditCard },
            { id: 'pending-approvals', label: '📋 طلباتي (سندات/تغييرات)', icon: FileText },
            { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 },
          ],
        },
      ];
    }

    return [
      ...baseItems,
      // Daily Operations Category
      {
        category: 'operations',
        label: t('operationsManagement'),
        items: [
            { id: 'clients', label: t('clients'), icon: Users },
            { id: 'sales-pipeline', label: '📋 Sales Pipeline', icon: Users },
            { id: 'crm-dashboard', label: '📊 لوحة CRM', icon: BarChart3 },
            { id: 'bookings', label: t('bookings'), icon: Package },
            { id: 'tickets', label: '✈️ التذاكر', icon: FileText },
            { id: 'organized-trips', label: '🗺️ الرحلات المنظمة', icon: MapPin },
            { id: 'umrah-groups', label: '🛫 إدارة الأفواج (حج/عمرة/سياحية/أخرى)', icon: Users },
            { id: 'suppliers', label: t('suppliers'), icon: Building2 },
            { id: 'dailyOperations', label: t('dailyOperations'), icon: FileText }
        ]
      },
      // Financial Transactions Category (LIMITED)
      {
        category: 'financial',
        label: t('financialManagement'),
        items: [
          { id: 'payments', label: '💰 المدفوعات (العمليات)', icon: CreditCard },
          { id: 'financial-management', label: '💰 الإدارة المالية', icon: CreditCard },
          { id: 'cash-transfers-inbox', label: '📤 تحويلاتي النقدية', icon: CreditCard },
          { id: 'general-accounting', label: '📋 محاسبة الإدارة العامة', icon: CreditCard },
          { id: 'pending-approvals', label: '📋 طلباتي (سندات/تغييرات)', icon: FileText },
          { id: 'simple-installments', label: '📋 نظام الأقساط', icon: CalendarIcon },
          { id: 'refunds', label: '↩️ إدارة الاسترجاع', icon: CreditCard },
          { id: 'dailyOperationsReports', label: t('dailyOperationsReports'), icon: BarChart3 }
          // REMOVED: invoices (restricted to managers/accountants)
        ]
      },
      // Agency Information Category
      {
        category: 'agencyInfo',
        label: 'معلومات الوكالة',
        items: [
          { id: 'agencySettings', label: t('agencySettings') + ' (للعرض فقط)', icon: Building2 }
        ]
      }
    ];
  };

  const navItems = getNavItems();

  // Get role display name
  const getRoleDisplay = (role) => {
    switch(role) {
      case 'super_admin': return t('superAdmin');
      case 'general_accountant': return t('generalAccountant');
      case 'general_manager': return 'مسؤول عام';
      case 'services_manager': return 'مسؤول الخدمات';
      case 'agency_staff': return t('agencyStaff');
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Professional Sidebar Navigation */}
      <aside
        className={`fixed md:relative top-0 right-0 bottom-0 z-40 w-72 md:w-64 max-w-[85vw] bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 md:translate-x-0 overflow-y-auto flex flex-col ${sidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}`}
        data-testid="app-sidebar"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-lg font-bold text-gray-900 text-center">
            🏢 وكالات صنهاجة للسفر
          </h1>
          <p className="text-sm text-gray-600 text-center mt-1">غرب الجزائر</p>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-200 bg-brand-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-brand-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900 text-sm">{user?.name}</p>
              <p className="text-xs text-brand-600">{getRoleDisplay(user?.role)}</p>
              {user?.is_subagent && (
                <p
                  className="text-[10px] mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 inline-block font-semibold"
                  data-testid="subagent-badge"
                >
                  🤝 وكيل مُناوِل
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4">
          {navItems.map((item, index) => (
            <div key={index}>
              {item.category === 'main' ? (
                // Main dashboard item
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center px-4 py-3 text-right hover:bg-brand-50 hover:text-brand-600 transition-colors ${
                    activeTab === item.id ? 'bg-brand-100 text-brand-600 border-r-4 border-brand-600' : 'text-gray-700'
                  }`}
                >
                  <div className={`
                    w-8 h-8 bg-gradient-to-br from-brand-400 to-brand-600 
                    rounded-lg flex items-center justify-center text-white shadow-lg
                    transform transition-all duration-300 ease-in-out
                    hover:scale-110 hover:-translate-y-1 hover:shadow-xl active:scale-95
                    ${activeTab === item.id ? 'ring-2 ring-brand-300 scale-105 from-brand-500 to-brand-700' : ''}
                  `} style={{ filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))' }}>
                    <item.icon className="h-4 w-4" />
                  </div>
                  <span className="font-medium mr-3">{item.label}</span>
                </button>
              ) : (
                // Category sections
                <div className="mt-6">
                  <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {item.label}
                  </h3>
                  {item.items?.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => setActiveTab(subItem.id)}
                      className={`w-full flex items-center px-6 py-2 text-right transition-colors ${
                        subItem.isHighlighted
                          ? (activeTab === subItem.id
                              ? 'bg-sky-100 text-sky-800 border-r-4 border-sky-600 font-bold'
                              : 'bg-sky-50/40 text-sky-800 hover:bg-sky-100 font-semibold')
                          : (activeTab === subItem.id
                              ? 'bg-brand-100 text-brand-600 border-r-4 border-brand-600'
                              : 'text-gray-600 hover:bg-brand-50 hover:text-brand-600')
                      }`}
                    >
                      <div className={`
                        w-6 h-6 bg-gradient-to-br ${subItem.customGradient || 'from-brand-300 to-brand-500'}
                        rounded-md flex items-center justify-center text-white shadow-md
                        transform transition-all duration-200 ease-in-out
                        hover:scale-110 hover:shadow-lg active:scale-95
                        ${activeTab === subItem.id ? 'ring-1 ring-brand-200 scale-105' : ''}
                      `}>
                        <subItem.icon className="h-3 w-3" />
                      </div>
                      <span className="text-sm mr-3">{subItem.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-xs"
            >
              <Globe className="h-4 w-4" />
              <span>{language === 'ar' ? 'Français' : 'العربية'}</span>
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="flex items-center space-x-2 text-red-600 hover:text-red-700 text-xs"
            >
              <LogOut className="h-4 w-4" />
              <span>{t('logout')}</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 -mr-1 text-gray-700 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors flex-shrink-0"
                aria-label="فتح القائمة"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
                  {navItems
                    .flatMap(item => item.items ? item.items : [item])
                    .find(item => item.id === activeTab)?.label || t('dashboard')}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  {formatDateWithEnglishNumerals(new Date(), {
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {/* Notifications Bell */}
              <NotificationBell />
              
              {/* Quick Actions could go here */}
              <div className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                📅 {formatTimeWithEnglishNumerals(new Date(), {hour: '2-digit', minute: '2-digit'})}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-6 overflow-auto">
          {children}
        </main>
      </div>

      {/* Floating WhatsApp Button — fixed bottom-left, opens in NEW TAB */}
      <WhatsAppFAB />
    </div>
  );
};

// Profile Component for Google OAuth Callback
const Profile = () => {
  const { handleGoogleCallback } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const processGoogleCallback = async () => {
      try {
        // Extract session ID from URL fragment
        const hash = window.location.hash;
        console.log('Current hash:', hash);
        
        if (hash) {
          const params = new URLSearchParams(hash.substring(1));
          const sessionId = params.get('session_id');
          
          console.log('Extracted session ID:', sessionId);
          
          if (sessionId) {
            console.log('Processing Google callback with session ID:', sessionId);
            const result = await handleGoogleCallback(sessionId);
            
            if (result.success) {
              console.log('Google authentication successful, redirecting to dashboard');
              navigate('/');
            } else {
              setError(result.error || 'Google authentication failed');
            }
          } else {
            setError('No session ID found in callback');
          }
        } else {
          setError('No authentication data received');
        }
      } catch (error) {
        console.error('Profile callback error:', error);
        setError('Authentication process failed');
      } finally {
        setLoading(false);
      }
    };

    processGoogleCallback();
  }, [handleGoogleCallback, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">جاري معالجة تسجيل الدخول...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في التسجيل</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/login')} className="w-full">
              العودة لتسجيل الدخول
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

// Notification Bell Component
const NotificationBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    transfers: { count: 0, items: [] },
    installments: { count: 0, items: [] },
    flights: { count: 0, items: [] },
    suppliers: { count: 0, items: [] },
    airline_payments: { count: 0, items: [] },
    missing_receipts: { count: 0, items: [] },
    unpaid_operations: { count: 0, items: [] },
    pending_approvals: { count: 0, items: [] },
    total: 0,
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTabLocal] = useState('all');

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/notifications/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({
        transfers: r.data?.transfers || { count: 0, items: [] },
        installments: r.data?.installments || { count: 0, items: [] },
        flights: r.data?.flights || { count: 0, items: [] },
        suppliers: r.data?.suppliers || { count: 0, items: [] },
        airline_payments: r.data?.airline_payments || { count: 0, items: [] },
        missing_receipts: r.data?.missing_receipts || { count: 0, items: [] },
        unpaid_operations: r.data?.unpaid_operations || { count: 0, items: [] },
        pending_approvals: r.data?.pending_approvals || { count: 0, items: [] },
        total: r.data?.total || 0,
      });
    } catch (e) {
      // silent — bell stays empty on auth/network failure
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000); // poll every 30s
    return () => clearInterval(id);
  }, []);

  const unreadCount = data.total;

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
      case 'info': return <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />;
      default: return <Bell className="h-4 w-4 text-gray-500 flex-shrink-0" />;
    }
  };

  const handleDeepLink = (deepLink) => {
    setShowNotifications(false);
    try {
      if (deepLink === 'financial:transfers') {
        sessionStorage.setItem('pending-open-tab', 'financial');
        sessionStorage.setItem('pending-open-section', 'transfers');
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'financial' }));
      } else if (deepLink === 'installments') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'simple-installments' }));
      } else if (deepLink === 'flights') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'flights-management' }));
      } else if (deepLink === 'suppliers') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'suppliers' }));
      } else if (deepLink === 'daily-operations') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'dailyOperations' }));
      } else if (deepLink === 'pending-approvals') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'pending-approvals' }));
      }
      navigate('/dashboard');
    } catch (_) { /* no-op */ }
  };

  // Build combined list based on active tab
  const buildItems = () => {
    if (activeTab === 'transfers') return data.transfers.items;
    if (activeTab === 'installments') return data.installments.items;
    if (activeTab === 'flights') return data.flights.items;
    if (activeTab === 'suppliers') return data.suppliers.items;
    if (activeTab === 'airline_payments') return data.airline_payments.items;
    if (activeTab === 'missing_receipts') return data.missing_receipts.items;
    if (activeTab === 'unpaid_operations') return data.unpaid_operations.items;
    if (activeTab === 'pending_approvals') return data.pending_approvals.items;
    // all — merge in priority order (errors first → today's flights, overdue installments/airline payments, then transfers, then upcoming flights/installments, then suppliers, finally missing receipts)
    return [
      ...data.pending_approvals.items.filter(i => i.severity === 'error'),
      ...data.unpaid_operations.items.filter(i => i.severity === 'error'),
      ...data.flights.items.filter(i => i.severity === 'error'),
      ...data.airline_payments.items.filter(i => i.severity === 'error'),
      ...data.installments.items.filter(i => i.is_overdue),
      ...data.pending_approvals.items.filter(i => i.severity !== 'error'),
      ...data.transfers.items,
      ...data.unpaid_operations.items.filter(i => i.severity !== 'error'),
      ...data.airline_payments.items.filter(i => i.severity !== 'error'),
      ...data.flights.items.filter(i => i.severity !== 'error'),
      ...data.installments.items.filter(i => !i.is_overdue),
      ...data.suppliers.items,
      ...data.missing_receipts.items,
    ];
  };

  const items = buildItems();

  const tabConfig = [
    { id: 'all', label: 'الكل', count: data.total, icon: <Bell className="h-3 w-3" /> },
    { id: 'pending_approvals', label: 'موافقات', count: data.pending_approvals.count, icon: '📥' },
    { id: 'unpaid_operations', label: 'غير مدفوعة', count: data.unpaid_operations.count, icon: '💸' },
    { id: 'transfers', label: 'تحويلات', count: data.transfers.count, icon: '⏳' },
    { id: 'installments', label: 'أقساط', count: data.installments.count, icon: '📅' },
    { id: 'airline_payments', label: 'دفعات طيران', count: data.airline_payments.count, icon: '💳' },
    { id: 'missing_receipts', label: 'وصولات ناقصة', count: data.missing_receipts.count, icon: '🧾' },
    { id: 'flights', label: 'رحلات', count: data.flights.count, icon: '✈️' },
    { id: 'suppliers', label: 'موردون', count: data.suppliers.count, icon: '💰' },
  ];

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
        data-testid="notification-bell-trigger"
      >
        <Bell className={`h-5 w-5 ${unreadCount > 0 ? 'animate-pulse text-amber-600' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center font-bold" data-testid="notification-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showNotifications && (
        <div className="fixed sm:absolute top-16 sm:top-auto inset-x-2 sm:inset-x-auto sm:left-0 sm:mt-2 sm:w-[420px] bg-white rounded-lg shadow-xl border border-gray-200 z-50" dir="rtl" data-testid="notification-panel">
          <div className="p-3 border-b border-gray-200 bg-gradient-to-l from-amber-50 to-white">
            <h3 className="font-bold text-gray-900 text-right flex items-center gap-2 text-sm">
              <Bell className="h-4 w-4 text-amber-600" /> مركز الإشعارات
              {data.total > 0 && (
                <span className="mr-auto text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">
                  {data.total} تنبيه
                </span>
              )}
            </h3>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto" data-testid="notification-tabs">
            {tabConfig.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTabLocal(tab.id)}
                className={`flex-1 min-w-[70px] px-2 py-2 text-xs font-semibold transition-colors flex flex-col items-center gap-0.5 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-amber-700 border-amber-500 bg-white'
                    : 'text-gray-600 border-transparent hover:bg-gray-100'
                }`}
                data-testid={`notification-tab-${tab.id}`}
              >
                <span className="flex items-center gap-1">
                  {typeof tab.icon === 'string' ? <span>{tab.icon}</span> : tab.icon}
                  <span>{tab.label}</span>
                </span>
                {tab.count > 0 && (
                  <span className={`text-[10px] px-1.5 rounded-full font-bold ${
                    activeTab === tab.id ? 'bg-amber-500 text-white' : 'bg-gray-300 text-gray-700'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                ✅ لا توجد تنبيهات في هذا القسم
              </div>
            ) : (
              items.map((n, idx) => (
                <div
                  key={`${activeTab}-${n.id}-${idx}`}
                  className="p-3 border-b border-gray-100 hover:bg-amber-50 cursor-pointer transition-colors"
                  onClick={() => handleDeepLink(n.deep_link)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {getSeverityIcon(n.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900 truncate">{n.title}</div>
                      <div className="text-xs text-gray-600 mt-0.5 truncate">{n.message}</div>
                      <div className={`text-[10px] mt-0.5 font-medium ${
                        n.severity === 'error' ? 'text-red-600' :
                        n.severity === 'warning' ? 'text-amber-700' : 'text-gray-400'
                      }`}>
                        {n.age}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {data.total > 0 && (
            <div className="p-2 border-t border-gray-100 text-center bg-gray-50 text-[11px] text-gray-500">
              يتم التحديث تلقائياً كل 30 ثانية
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main App Component
const PayrollWithUser = ({ activeTab, setActiveTab }) => {
  const { user } = useContext(AuthContext);
  return <PayrollManagement user={user} activeTab={activeTab} setActiveTab={setActiveTab} />;
};

const ClientDossierWithUser = ({ activeTab, setActiveTab }) => {
  const { user } = useContext(AuthContext);
  return <ClientDossierManagement user={user} activeTab={activeTab} setActiveTab={setActiveTab} />;
};

const MainApp = ({ activeTab, setActiveTab }) => {
  // Allow components to navigate via window event (e.g., DailyOperations → SimpleInstallments after conversion)
  React.useEffect(() => {
    const handler = (e) => { if (e.detail) setActiveTab(e.detail); };
    window.addEventListener('navigate-to-tab', handler);
    return () => window.removeEventListener('navigate-to-tab', handler);
  }, [setActiveTab]);

  // Auto-attach resize handles + saved widths to every <table> on the page
  React.useEffect(() => {
    initResizableColumns();
  }, []);

  const components = {
    dashboard: (props) => <Dashboard {...props} setActiveTab={setActiveTab} />,
    clients: ClientsManagement,
    suppliers: SuppliersManagement,
    bookings: BookingsManagement,
    tickets: TicketsManagement,
    'organized-trips': OrganizedTripsManagement,
    'umrah-groups': UmrahGroupManagement,
    'smart-archive': SmartArchiveManagement,
    'ops-hub': OperationsHub,
    'secure-backup': SecureBackupCenter,
    'full-backup-restore': BackupRestore,
    'security-center': SecurityCenter,
    invoices: InvoicesManagement,
    payments: PaymentsManagement,
    'financial-management': (props) => <FinancialManagement {...props} setActiveTab={setActiveTab} />,
    'cash-transfers-inbox': CashTransfersInboxPage,
    'archive-management': ArchiveManagement,
    'cashbox': (props) => <div className="p-4 text-center">🏦 الخزينة متاحة في الإدارة المالية</div>,
    'general-accounting': (props) => <GeneralAccountingManagement {...props} setActiveTab={setActiveTab} />,
    'credit-notes': CreditNotesManagement,
    'pending-approvals': PendingApprovalsInbox,
    'expense-aggregator': ExpenseAggregator,
    'foreign-exchange': (props) => <ForeignExchangeManagement {...props} setActiveTab={setActiveTab} />,
    'simple-installments': (props) => <SimpleInstallmentsManagement {...props} setActiveTab={setActiveTab} />,
    'installments-by-agency': (props) => <InstallmentsByAgencyOverview {...props} />,
    'refunds': (props) => <RefundManagement {...props} setActiveTab={setActiveTab} />,
    payroll: PayrollWithUser,
    'client-dossier': ClientDossierWithUser,
    reports: ReportsManagement,
    userManagement: UserManagement,
    agencyManagement: AgencyManagement,
    dailyReports: DailyReportsManagement,
    servicesManagement: ServicesManagement,
    'hierarchical-services': HierarchicalServicesManagement,
    'crm-dashboard': CRMDashboard,
    'sales-pipeline': SalesPipeline,
    agencySettings: AgencySettingsManagement,
    pdfSettings: PDFSettingsManagement,
    excelSettings: ExcelSettingsManagement,
    transferAuditTrail: TransferAuditTrail,
    financialTraceability: FinancialTraceability,
    documentCenter: DocumentCenter,
    'season-reset': SeasonReset,
    'subagents-management': SubagentsManagement,
    'flights-management': FlightsManagement,
    'create-transfer-batch': CreateTransferBatch,
    dailyOperations: DailyOperationsManagement,
    dailyOperationsReports: DailyOperationsReports
  };
  
  const Component = components[activeTab] || components.dashboard;
  return <Component activeTab={activeTab} setActiveTab={setActiveTab} />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Main App Component  
function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Public verification page (used by QR codes on every PDF).
                  No auth required — customers scan the QR with their phone
                  and land here directly. */}
              <Route path="/verify/:docId" element={<VerifyDocument />} />
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout activeTab={activeTab} setActiveTab={setActiveTab}>
                      <MainApp activeTab={activeTab} setActiveTab={setActiveTab} />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
            {/* Global Shadcn-based PDF/Excel format picker — replaces the
                Chrome-blocked window.confirm() in utils/pdfPrint.js. */}
            <FormatPickerDialog />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;